import React, { useState } from "react";
import { Link } from "react-router-dom";
import hplcSystem from "../../images/analytical_hplc_system.png";
import dissolutionTester from "../../images/analytical_dissolution_tester.png";

export default function AnalyticalServicesRedesign() {
  const [activeTab, setActiveTab] = useState("chromatography");

  const coreServices = [
    {
      title: "Analytical Method Development",
      description: "Developing robust, precise, and optimized analytical methodologies tailored to simple, complex, or unstable drug substances.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      )
    },
    {
      title: "Analytical Method Validation",
      description: "Providing complete validation protocols complying with ICH guidelines to ensure precision, accuracy, specificity, and ruggedness.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    }
  ];

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800">
      {/* Hero Header */}
      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-blue-deep">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 mix-blend-overlay"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1532187643603-ba119ca4109e?q=80&w=1920&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Analytical Science
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            Analytical Services
          </h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-slate-300 font-light leading-relaxed">
            Completely integrated method development, validation, and characterization. Our expertise ensures reliable timelines for highly complex and unstable drug products.
          </p>
        </div>
      </section>

      {/* Overview Integration Banner */}
      <section className="py-16 max-w-7xl mx-auto px-6 md:px-12">
        <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-12 shadow-sm flex flex-col lg:flex-row items-center gap-8">
          <div className="lg:w-2/3 space-y-4">
            <div className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full">
              Integrated R&amp;D Team
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-brand-blue tracking-tight">
              Effective &amp; Accelerated Product Development
            </h2>
            <p className="text-slate-600 font-light text-sm md:text-base leading-relaxed">
              Our Analytical team is completely integrated with Formulation and Quality Assurance scientists. Working hand-in-hand in team configurations, we accelerate development cycles, ensure early identification of degradation pathways, and build high standards of regulatory compliance into every dosage form from step one.
            </p>
          </div>
          <div className="lg:w-1/3 w-full bg-slate-50 border border-slate-100 p-6 rounded-2xl flex flex-col justify-center">
            <h4 className="text-xs font-bold text-brand-teal uppercase tracking-wider mb-3">Our Core Mandate</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-xs font-semibold text-brand-blue">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-teal shrink-0" />
                <span>Chemistry &amp; Formulations Alignment</span>
              </div>
              <div className="flex items-center space-x-2 text-xs font-semibold text-brand-blue">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-teal shrink-0" />
                <span>Accelerated Project Timelines</span>
              </div>
              <div className="flex items-center space-x-2 text-xs font-semibold text-brand-blue">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-teal shrink-0" />
                <span>QbD-Driven Performance Standards</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-8 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-brand-blue tracking-tight">
            Method Development &amp; Validation
          </h2>
          <p className="text-slate-500 font-light text-sm mt-2">
            Providing full regulatory-compliant analytical data to guarantee the identity, quality, and purity of drug components.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {coreServices.map((service, idx) => (
            <div 
              key={idx}
              className="bg-white border border-slate-100 hover:border-brand-teal/25 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 flex items-start space-x-6"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-teal-soft flex items-center justify-center text-brand-teal shrink-0">
                {service.icon}
              </div>
              <div>
                <h3 className="text-lg font-bold text-brand-blue">{service.title}</h3>
                <p className="text-slate-600 font-light text-sm mt-2 leading-relaxed">{service.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Interactive Tabs Section */}
      <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full mb-3">
            Analytical Showcase
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-brand-blue tracking-tight">
            State-of-the-Art Laboratory Support
          </h2>
          <p className="text-slate-500 font-light mt-3">
            Explore our specialized categories, customized instrument configurations, and regulatory testing methodologies.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex justify-center mb-12">
          <div className="bg-slate-100 p-1.5 rounded-full flex flex-wrap justify-center gap-1">
            <button 
              onClick={() => setActiveTab("chromatography")}
              className={`px-5 py-2 rounded-full text-xs md:text-sm font-semibold transition-all ${activeTab === "chromatography" ? "bg-brand-blue text-white" : "text-slate-600 hover:text-brand-blue"}`}
            >
              Chromatography &amp; Validation
            </button>
            <button 
              onClick={() => setActiveTab("dissolution")}
              className={`px-5 py-2 rounded-full text-xs md:text-sm font-semibold transition-all ${activeTab === "dissolution" ? "bg-brand-blue text-white" : "text-slate-600 hover:text-brand-blue"}`}
            >
              Equivalence &amp; Dissolution
            </button>
            <button 
              onClick={() => setActiveTab("stability")}
              className={`px-5 py-2 rounded-full text-xs md:text-sm font-semibold transition-all ${activeTab === "stability" ? "bg-brand-blue text-white" : "text-slate-600 hover:text-brand-blue"}`}
            >
              Stability &amp; API Profiling
            </button>
          </div>
        </div>

        {/* Tab Content Box */}
        <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-12 shadow-md relative min-h-[400px]">
          
          {/* Tab 1: Chromatography & Validation */}
          {activeTab === "chromatography" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 animate-fade-in">
              <div className="lg:col-span-7 space-y-8">
                <div className="border-b border-slate-100 pb-6">
                  <span className="inline-block text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2.5 py-1 rounded-full mb-3">
                    HPLC &amp; Method Development
                  </span>
                  <h3 className="text-2xl font-bold text-brand-blue">Chromatographic Analysis &amp; QbD Optimization</h3>
                  <p className="text-slate-500 font-light text-sm mt-1">High-resolution liquid chromatography supporting robust validation and transfer protocols.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                    <h4 className="text-base font-bold text-brand-blue">QbD Method Development</h4>
                    <p className="text-slate-600 font-light text-xs md:text-sm mt-1.5 leading-relaxed">
                      Development and fine tuning of analytical methods with Quality by Design (QbD) approach, ensuring reliable method robustness.
                    </p>
                  </div>
                  <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                    <h4 className="text-base font-bold text-brand-blue">Method Validation &amp; Transfer</h4>
                    <p className="text-slate-600 font-light text-xs md:text-sm mt-1.5 leading-relaxed">
                      Seamless method transfers and comprehensive cleaning method validations utilizing modern instrumentation templates.
                    </p>
                  </div>
                  <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                    <h4 className="text-base font-bold text-brand-blue">Cleaning Method Validation</h4>
                    <p className="text-slate-600 font-light text-xs md:text-sm mt-1.5 leading-relaxed">
                      Highly sensitive validations to assure thorough equipment decontamination between pharmaceutical product runs.
                    </p>
                  </div>
                  <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                    <h4 className="text-base font-bold text-brand-blue">Short Run Time Methods</h4>
                    <p className="text-slate-600 font-light text-xs md:text-sm mt-1.5 leading-relaxed">
                      Development of cost-effective and shortest run time methods to streamline analytical workflows and maximize output.
                    </p>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-5 flex flex-col justify-center">
                <div className="relative group overflow-hidden rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300">
                  <img 
                    src={hplcSystem} 
                    alt="HPLC Chromatography Equipment" 
                    className="w-full h-[320px] lg:h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-85" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-[10px] font-semibold text-brand-teal uppercase tracking-wider">HPLC Instrumentation</p>
                    <h4 className="text-sm font-bold mt-0.5">High Performance Liquid Chromatograph</h4>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Equivalence & Dissolution */}
          {activeTab === "dissolution" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 animate-fade-in">
              <div className="lg:col-span-7 space-y-8">
                <div className="border-b border-slate-100 pb-6">
                  <span className="inline-block text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2.5 py-1 rounded-full mb-3">
                    USP Dissolution &amp; Equivalence
                  </span>
                  <h3 className="text-2xl font-bold text-brand-blue">Pharmaceutical Equivalence &amp; Profiling</h3>
                  <p className="text-slate-500 font-light text-sm mt-1">Multi-vessel USP dissolution testing to map innovator release curves and reverse engineer compounds.</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-4 bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                    <div className="w-8 h-8 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0 mt-1 font-bold text-sm">1</div>
                    <div>
                      <h4 className="text-base font-bold text-brand-blue">Dissolution Profile Mapping</h4>
                      <p className="text-slate-600 font-light text-xs md:text-sm mt-1 leading-relaxed">
                        Comprehensive dissolution profile mapping and multimedia dissolution profiling to establish bio-equivalence profiles.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                    <div className="w-8 h-8 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0 mt-1 font-bold text-sm">2</div>
                    <div>
                      <h4 className="text-base font-bold text-brand-blue">Reverse Engineering of Innovator</h4>
                      <p className="text-slate-600 font-light text-xs md:text-sm mt-1 leading-relaxed">
                        Deformulation studies, innovator product characterization, and reference product profiling to recreate similar therapeutic performance.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                    <div className="w-8 h-8 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0 mt-1 font-bold text-sm">3</div>
                    <div>
                      <h4 className="text-base font-bold text-brand-blue">Pharmaceutical Equivalence</h4>
                      <p className="text-slate-600 font-light text-xs md:text-sm mt-1 leading-relaxed">
                        Robust multimedia evaluation and comparative parameters ensuring strict dosage bio-equivalence.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-5 flex flex-col justify-center">
                <div className="relative group overflow-hidden rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300">
                  <img 
                    src={dissolutionTester} 
                    alt="Dissolution Testing Machinery" 
                    className="w-full h-[320px] lg:h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-85" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-[10px] font-semibold text-brand-teal uppercase tracking-wider">USP Dissolution Apparatus</p>
                    <h4 className="text-sm font-bold mt-0.5">Multi-Vessel USP Dissolution Tester</h4>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 3: Stability & API Profiling */}
          {activeTab === "stability" && (
            <div className="space-y-8 animate-fade-in">
              <div className="border-b border-slate-100 pb-6">
                <span className="inline-block text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2.5 py-1 rounded-full mb-3">
                  ICH Guidelines Compliance
                </span>
                <h3 className="text-2xl font-bold text-brand-blue">ICH Stability &amp; API Characterization</h3>
                <p className="text-slate-500 font-light text-sm mt-1">Reliable environmental profiling and stability indicating method development.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                  <h4 className="text-base font-bold text-brand-blue">API Characterization</h4>
                  <p className="text-slate-600 font-light text-xs md:text-sm mt-2 leading-relaxed">
                    Comprehensive profiling including solubility mapping in buffers and organic solvents, particle behavior, and chemical behavior.
                  </p>
                </div>
                <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                  <h4 className="text-base font-bold text-brand-blue">Forced Degradation</h4>
                  <p className="text-slate-600 font-light text-xs md:text-sm mt-2 leading-relaxed">
                    Stress testing across pH, thermal, light, and oxidation levels to map degradation patterns and optimize formulation pathways.
                  </p>
                </div>
                <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                  <h4 className="text-base font-bold text-brand-blue">Stability Indicating Methods</h4>
                  <p className="text-slate-600 font-light text-xs md:text-sm mt-2 leading-relaxed">
                    Development of reliable, consistent, stability-indicating analytical methods to track assay potency and purity over time.
                  </p>
                </div>
                <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                  <h4 className="text-base font-bold text-brand-blue">ICH Stability Studies</h4>
                  <p className="text-slate-600 font-light text-xs md:text-sm mt-2 leading-relaxed">
                    Comprehensive photo-stability and long-term storage stability mapping in accordance with international ICH requirements.
                  </p>
                </div>
              </div>

              {/* Regulatory banner footer inside tab */}
              <div className="bg-brand-blue-deep text-white p-6 rounded-2xl grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
                <div className="border-b md:border-b-0 md:border-r border-white/10 pb-4 md:pb-0 md:pr-6">
                  <h5 className="text-xs font-bold text-brand-teal-light uppercase">Regulatory Dossiers</h5>
                  <p className="text-xs font-semibold mt-1">Full analytical documentation and regulatory support packages.</p>
                </div>
                <div className="border-b md:border-b-0 md:border-r border-white/10 pb-4 md:pb-0 md:px-6">
                  <h5 className="text-xs font-bold text-brand-teal-light uppercase">Specification Mapping</h5>
                  <p className="text-xs font-semibold mt-1">Defining drug product validation limits and shelf-life profiles.</p>
                </div>
                <div className="md:pl-6">
                  <h5 className="text-xs font-bold text-brand-teal-light uppercase">Quality Standards</h5>
                  <p className="text-xs font-semibold mt-1">Ensuring potency, purity, stability, and safety limits are fully mapped.</p>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Inquiry CTA Section */}
      <section className="bg-white border-t border-slate-100 py-16 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h3 className="text-2xl md:text-3xl font-bold text-brand-blue mb-4">
            Partner with Our Integrated R&amp;D Team
          </h3>
          <p className="text-slate-600 font-light text-sm md:text-base max-w-2xl mx-auto mb-8">
            Let us support your analytical method development, QbD optimization, and bio-equivalence mapping needs under strict quality timelines.
          </p>
          <Link
            to="/ContactUs"
            state={{ from: "Inquiry: Analytical Services" }}
            className="inline-flex items-center justify-center bg-brand-blue hover:bg-brand-blue-light text-white font-bold px-8 py-3.5 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 text-sm"
          >
            <span>Inquire About Analytical Services</span>
            <svg className="w-4 h-4 ml-2 animate-bounce-horizontal" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}
